/**
 * API Utility Module
 * 
 * Provides centralized API calling functionality with consistent error handling
 * and response formatting for the new backend API structure.
 */

import type { ApiResponse, ApiError as ApiErrorType } from '../types';

/**
 * Configuration for API calls
 */
interface ApiConfig {
  baseUrl?: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
}

class ApiClient {
  private config: ApiConfig;

  constructor(config: ApiConfig = {}) {
    this.config = {
      baseUrl: config.baseUrl || '',
      timeout: config.timeout || 10000,
      defaultHeaders: {
        'Content-Type': 'application/json',
        ...config.defaultHeaders
      }
    };
  }

  /**
   * Make an API call with proper error handling
   */
  async call<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.config.defaultHeaders,
          ...options.headers
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(
          data.error?.code || 'HTTP_ERROR',
          data.error?.message || `HTTP ${response.status}: ${response.statusText}`,
          {
            status: response.status,
            statusText: response.statusText,
            url,
            ...data.error?.details
          }
        );
      }

      if (!data.success) {
        throw new ApiError(
          data.error?.code || 'API_ERROR',
          data.error?.message || 'API request failed',
          data.error?.details
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('TIMEOUT', 'Request timed out', { url, timeout: this.config.timeout });
        }
        
        throw new ApiError('NETWORK_ERROR', error.message, { url, originalError: error.message });
      }

      throw new ApiError('UNKNOWN_ERROR', 'An unknown error occurred', { url });
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    const url = params ? `${endpoint}?${new URLSearchParams(params)}` : endpoint;
    return this.call<T>(url, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.call<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.call<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.call<T>(endpoint, { method: 'DELETE' });
  }
}

/**
 * Custom API Error class
 */
class ApiError extends Error {
  public code: string;
  public details?: any;

  constructor(code: string, message: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

// Create default API client instance
// Use VITE_API_URL in production, fallback to localhost:3001 for development
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const apiClient = new ApiClient({
  baseUrl: API_BASE_URL,
  timeout: 30000 // 30 seconds timeout for slow MongoDB queries
});

// Export types and classes
export { ApiClient, ApiError };
export type { ApiConfig };

/**
 * Convenience functions for common API operations
 */

// Driver API calls
export const driverApi = {
  getAll: (zona?: string) => 
    apiClient.get('/api/drivers', zona ? { zona } : undefined),
  
  getById: (driverId: string) => 
    apiClient.get(`/api/drivers/${driverId}`),
  
  updateLocation: (driverId: string, lat: number, lon: number) =>
    apiClient.put(`/api/drivers/${driverId}/location`, { lat, lon }),
  
  getConnected: () => 
    apiClient.get('/api/drivers/connected'),
  
  getInArea: (lat: number, lon: number, radius: number) =>
    apiClient.get('/api/drivers/area', { 
      lat: lat.toString(), 
      lon: lon.toString(), 
      radius: radius.toString() 
    })
};

// Delivery API calls
export const deliveryApi = {
  getAll: (driverId?: string, status?: string) => {
    const params: Record<string, string> = {};
    if (driverId) params.driver_id = driverId;
    if (status) params.status = status;
    return apiClient.get('/api/deliveries', Object.keys(params).length ? params : undefined);
  },
  
  create: (deliveryData: any) =>
    apiClient.post('/api/deliveries', deliveryData),
  
  getById: (deliveryId: string) =>
    apiClient.get(`/api/deliveries/${deliveryId}`),
  
  updateStatus: (deliveryId: string, status: string, data?: any) =>
    apiClient.put(`/api/deliveries/${deliveryId}/status`, { status, ...data }),
  
  assign: (deliveryId: string, driverId: string) =>
    apiClient.put(`/api/deliveries/${deliveryId}/assign`, { driver_id: driverId }),
  
  reassign: (deliveryId: string, newDriverId: string) =>
    apiClient.put(`/api/deliveries/${deliveryId}/reassign`, { new_driver_id: newDriverId }),
  
  getPending: () =>
    apiClient.get('/api/deliveries/pending')
};

// Dispatcher API calls
export const dispatcherApi = {
  getDashboard: () =>
    apiClient.get('/api/dispatcher/dashboard'),
  
  getStatistics: (period?: string) =>
    apiClient.get('/api/dispatcher/statistics', period ? { period } : undefined),
  
  getDriversOverview: () =>
    apiClient.get('/api/dispatcher/drivers/overview'),
  
  getDeliveriesOverview: (status?: string, driverId?: string) => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (driverId) params.driver_id = driverId;
    return apiClient.get('/api/dispatcher/deliveries/overview', Object.keys(params).length ? params : undefined);
  },
  
  getAlerts: () =>
    apiClient.get('/api/dispatcher/alerts'),
  
  getSystemStatus: () =>
    apiClient.get('/api/dispatcher/system/status'),

  // Driver-specific endpoints
  getDrivers: (zona?: string) =>
    apiClient.get('/api/dispatcher/drivers', zona ? { zona } : undefined),
  
  getDriverDeliveries: (driverId: string, ruta?: string) => {
    const params: Record<string, string> = {};
    if (ruta) params.ruta = ruta;
    return apiClient.get(`/api/dispatcher/driver/${driverId}/deliveries`, Object.keys(params).length ? params : undefined);
  },
  
  getDriverOptimizedRoute: (driverId: string, currentLat?: number, currentLon?: number, ruta?: string) => {
    const params: Record<string, string> = {};
    if (currentLat !== undefined) params.current_lat = currentLat.toString();
    if (currentLon !== undefined) params.current_lon = currentLon.toString();
    if (ruta) params.ruta = ruta;
    return apiClient.get(`/api/dispatcher/driver/${driverId}/route/optimized`, Object.keys(params).length ? params : undefined);
  },
  
  getDriverRoutes: (driverId: string) =>
    apiClient.get(`/api/dispatcher/driver/${driverId}/routes`),
  
  getDriverRoute: (driverId: string, ruta?: string) => {
    const params: Record<string, string> = {};
    if (ruta) params.ruta = ruta;
    return apiClient.get(`/api/dispatcher/driver/${driverId}/route`, Object.keys(params).length ? params : undefined);
  },
  
  getStats: (zona?: string) =>
    apiClient.get('/api/dispatcher/statistics', zona ? { zona } : undefined),
  
  searchDrivers: (query: string, zona?: string) => {
    const params: Record<string, string> = { q: query };
    if (zona) params.zona = zona;
    return apiClient.get('/api/dispatcher/search', params);
  },

  reassignDelivery: (deliveryId: string, data: any) =>
    apiClient.post(`/api/dispatcher/delivery/${deliveryId}/reassign`, data)
};

// Route API calls
export const routeApi = {
  optimize: (startLocation: { lat: number; lon: number }, points: any[]) =>
    apiClient.post('/api/routes/optimize', { start_location: startLocation, points }),
  
  calculateTravelTime: (origin: { lat: number; lon: number }, destination: { lat: number; lon: number }) =>
    apiClient.post('/api/routes/travel-time', { origin, destination }),
  
  createDriverRoute: (driverId: string, deliveryIds: string[]) =>
    apiClient.post(`/api/routes/driver/${driverId}`, { delivery_ids: deliveryIds }),
  
  getDriverRoute: (driverId: string) =>
    apiClient.get(`/api/routes/driver/${driverId}`),
  
  recalculateRoute: (driverId: string, currentLat: number, currentLon: number) =>
    apiClient.put(`/api/routes/driver/${driverId}/recalculate`, { 
      current_lat: currentLat, 
      current_lon: currentLon 
    }),
  
  getStatus: () =>
    apiClient.get('/api/routes/status')
};

// Tariff API calls
export const tariffApi = {
  getAll: (params?: { active_only?: boolean; type?: string; commune?: string; client_id?: string }) =>
    apiClient.get('/api/tariffs', params as Record<string, string>),
  
  getById: (tariffId: string) =>
    apiClient.get(`/api/tariffs/${tariffId}`),
  
  create: (tariffData: any) =>
    apiClient.post('/api/tariffs', tariffData),
  
  update: (tariffId: string, tariffData: any) =>
    apiClient.put(`/api/tariffs/${tariffId}`, tariffData),
  
  delete: (tariffId: string) =>
    apiClient.delete(`/api/tariffs/${tariffId}`),
  
  activate: (tariffId: string) =>
    apiClient.post(`/api/tariffs/${tariffId}/activate`),
  
  deactivate: (tariffId: string) =>
    apiClient.post(`/api/tariffs/${tariffId}/deactivate`),
  
  calculatePayment: (data: {
    commune: string;
    client_id?: string;
    distance_km?: number;
    package_count?: number;
    is_weekend?: boolean;
    is_night?: boolean;
  }) =>
    apiClient.post('/api/tariffs/calculate', data)
};

// Payment API calls
export const paymentApi = {
  getAll: (params?: { driver_id?: string; status?: string; period_start?: string; period_end?: string }) =>
    apiClient.get('/api/payments', params as Record<string, string>),
  
  getById: (paymentId: string) =>
    apiClient.get(`/api/payments/${paymentId}`),
  
  calculate: (data: {
    driver_id: string;
    period_start: string;
    period_end: string;
    include_compensations?: boolean;
  }) =>
    apiClient.post('/api/payments/calculate', data),
  
  create: (data: {
    driver_id: string;
    period_start: string;
    period_end: string;
    include_compensations?: boolean;
  }) =>
    apiClient.post('/api/payments', data),
  
  addCompensation: (paymentId: string, data: {
    description: string;
    amount: number;
    reason: string;
  }) =>
    apiClient.post(`/api/payments/${paymentId}/compensation`, data),
  
  addDeduction: (paymentId: string, data: {
    amount: number;
    reason: string;
  }) =>
    apiClient.post(`/api/payments/${paymentId}/deduction`, data),
  
  preliquidate: (paymentId: string) =>
    apiClient.post(`/api/payments/${paymentId}/preliquidate`),
  
  liquidate: (paymentId: string) =>
    apiClient.post(`/api/payments/${paymentId}/liquidate`),
  
  markPaid: (paymentId: string) =>
    apiClient.post(`/api/payments/${paymentId}/mark-paid`),
  
  generatePreliquidation: (data: {
    period_start: string;
    period_end: string;
    driver_ids?: string[];
  }) =>
    apiClient.post('/api/payments/preliquidation/generate', data),
  
  getDriverStats: (driverId: string) =>
    apiClient.get(`/api/payments/driver/${driverId}/stats`),
  
  getPeriodSummary: (periodStart: string, periodEnd: string) =>
    apiClient.get('/api/payments/period/summary', { period_start: periodStart, period_end: periodEnd })
};


// Hub API calls
export const hubApi = {
  getAll: (params?: { operation_type?: string; status?: string }) =>
    apiClient.get('/api/hub/operations', params as Record<string, string>),
  
  getById: (operationId: string) =>
    apiClient.get(`/api/hub/operations/${operationId}`),
  
  createReception: (data: {
    packages: Array<{
      tracking_number: string;
      client: string;
      origin_address?: string;
      destination_address: string;
      weight_kg?: number;
      dimensions?: string;
      notes?: string;
    }>;
    created_by?: string;
    notes?: string;
  }) =>
    apiClient.post('/api/hub/operations/reception', data),
  
  createDispatch: (data: {
    packages: Array<{
      tracking_number: string;
      client: string;
      origin_address?: string;
      destination_address: string;
      weight_kg?: number;
      dimensions?: string;
      notes?: string;
    }>;
    driver_id: string;
    created_by?: string;
    notes?: string;
  }) =>
    apiClient.post('/api/hub/operations/dispatch', data),
  
  createTransfer: (data: {
    packages: Array<{
      tracking_number: string;
      client: string;
      origin_address?: string;
      destination_address: string;
      weight_kg?: number;
      dimensions?: string;
      notes?: string;
    }>;
    driver_id?: string;
    created_by?: string;
    notes?: string;
  }) =>
    apiClient.post('/api/hub/operations/transfer', data),
  
  createReturn: (data: {
    packages: Array<{
      tracking_number: string;
      client: string;
      origin_address?: string;
      destination_address: string;
      weight_kg?: number;
      dimensions?: string;
      notes?: string;
    }>;
    return_reason: string;
    created_by?: string;
    notes?: string;
  }) =>
    apiClient.post('/api/hub/operations/return', data),
  
  createAddressChange: (data: {
    packages: Array<{
      tracking_number: string;
      client: string;
      origin_address?: string;
      destination_address: string;
      weight_kg?: number;
      dimensions?: string;
      notes?: string;
    }>;
    new_address: string;
    created_by?: string;
    notes?: string;
  }) =>
    apiClient.post('/api/hub/operations/address-change', data),
  
  assignDriver: (operationId: string, driverId: string) =>
    apiClient.post(`/api/hub/operations/${operationId}/assign-driver`, { driver_id: driverId }),
  
  complete: (operationId: string) =>
    apiClient.post(`/api/hub/operations/${operationId}/complete`),
  
  cancel: (operationId: string, reason?: string) =>
    apiClient.post(`/api/hub/operations/${operationId}/cancel`, { reason }),
  
  searchByTracking: (trackingNumber: string) =>
    apiClient.get('/api/hub/operations/search', { tracking_number: trackingNumber }),
  
  getDriverOperations: (driverId: string) =>
    apiClient.get(`/api/hub/operations/driver/${driverId}`),
  
  getStatistics: () =>
    apiClient.get('/api/hub/statistics')
};

// Schedule API calls
export const scheduleApi = {
  // Driver Availability
  createAvailability: (data: {
    driver_id: string;
    date: string;
    start_time: string;
    end_time: string;
    notes?: string;
  }) =>
    apiClient.post('/api/schedule/availability', data),
  
  getDriverAvailability: (driverId: string, params?: { start_date?: string; end_date?: string }) =>
    apiClient.get(`/api/schedule/availability/driver/${driverId}`, params as Record<string, string>),
  
  getAvailableDrivers: (date: string) =>
    apiClient.get(`/api/schedule/availability/date/${date}`),
  
  updateAvailability: (availabilityId: string, data: any) =>
    apiClient.put(`/api/schedule/availability/${availabilityId}`, data),
  
  deleteAvailability: (availabilityId: string) =>
    apiClient.delete(`/api/schedule/availability/${availabilityId}`),
  
  // Route Bids
  createBid: (data: {
    route_id: string;
    date: string;
    total_deliveries: number;
    estimated_payment: number;
  }) =>
    apiClient.post('/api/schedule/bids', data),
  
  getOpenBids: (date?: string) => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    return apiClient.get('/api/schedule/bids/open', Object.keys(params).length ? params : undefined);
  },
  
  getBidsByDate: (date: string) =>
    apiClient.get(`/api/schedule/bids/date/${date}`),
  
  getDriverAssignedBids: (driverId: string) =>
    apiClient.get(`/api/schedule/bids/driver/${driverId}`),
  
  assignBidToDriver: (bidId: string, driverId: string) =>
    apiClient.post(`/api/schedule/bids/${bidId}/assign`, { driver_id: driverId }),
  
  completeBid: (bidId: string) =>
    apiClient.post(`/api/schedule/bids/${bidId}/complete`),
  
  cancelBid: (bidId: string) =>
    apiClient.post(`/api/schedule/bids/${bidId}/cancel`),
  
  // Attendance Confirmation
  createAttendanceConfirmation: (data: {
    driver_id: string;
    bid_id: string;
    date: string;
  }) =>
    apiClient.post('/api/schedule/attendance', data),
  
  confirmAttendance: (confirmationId: string, notes?: string) =>
    apiClient.post(`/api/schedule/attendance/${confirmationId}/confirm`, { notes }),
  
  markNoShow: (confirmationId: string, notes?: string) =>
    apiClient.post(`/api/schedule/attendance/${confirmationId}/no-show`, { notes }),
  
  getPendingConfirmations: (date: string) =>
    apiClient.get(`/api/schedule/attendance/pending/${date}`),
  
  getDriverConfirmations: (driverId: string, date: string) =>
    apiClient.get(`/api/schedule/attendance/driver/${driverId}/${date}`)
};

// Polygon API calls
export const polygonApi = {
  // Polygons
  createPolygon: (data: {
    name: string;
    coordinates: Array<{ lat: number; lon: number }>;
    description?: string;
    color?: string;
    created_by?: string;
  }) =>
    apiClient.post('/api/polygons', data),
  
  getAllPolygons: (activeOnly?: boolean) => {
    const params: Record<string, string> = {};
    if (activeOnly) params.active_only = 'true';
    return apiClient.get('/api/polygons', Object.keys(params).length ? params : undefined);
  },
  
  getPolygonById: (polygonId: string) =>
    apiClient.get(`/api/polygons/${polygonId}`),
  
  updatePolygon: (polygonId: string, data: any) =>
    apiClient.put(`/api/polygons/${polygonId}`, data),
  
  deletePolygon: (polygonId: string) =>
    apiClient.delete(`/api/polygons/${polygonId}`),
  
  activatePolygon: (polygonId: string) =>
    apiClient.post(`/api/polygons/${polygonId}/activate`),
  
  deactivatePolygon: (polygonId: string) =>
    apiClient.post(`/api/polygons/${polygonId}/deactivate`),
  
  // Delivery Plans
  createPlan: (data: {
    name: string;
    polygon_id: string;
    effective_date?: string;
    notes?: string;
    created_by?: string;
  }) =>
    apiClient.post('/api/polygons/plans', data),
  
  getAllPlans: (activeOnly?: boolean) => {
    const params: Record<string, string> = {};
    if (activeOnly) params.active_only = 'true';
    return apiClient.get('/api/polygons/plans', Object.keys(params).length ? params : undefined);
  },
  
  getPlanById: (planId: string) =>
    apiClient.get(`/api/polygons/plans/${planId}`),
  
  addDriverToPlan: (planId: string, driverId: string, driverName: string) =>
    apiClient.post(`/api/polygons/plans/${planId}/driver/${driverId}`, { driver_name: driverName }),
  
  removeDriverFromPlan: (planId: string, driverId: string) =>
    apiClient.delete(`/api/polygons/plans/${planId}/driver/${driverId}`),
  
  activatePlan: (planId: string) =>
    apiClient.post(`/api/polygons/plans/${planId}/activate`),
  
  // Driver Assignments
  assignDriverToPolygon: (data: {
    driver_id: string;
    driver_name?: string;
    polygon_id: string;
    assigned_date?: string;
    notes?: string;
  }) =>
    apiClient.post('/api/polygons/assignments', data),
  
  getAllAssignments: () =>
    apiClient.get('/api/polygons/assignments'),
  
  getDriverAssignments: (driverId: string) =>
    apiClient.get(`/api/polygons/assignments/driver/${driverId}`),
  
  getPolygonAssignments: (polygonId: string) =>
    apiClient.get(`/api/polygons/assignments/polygon/${polygonId}`),
  
  deactivateAssignment: (assignmentId: string) =>
    apiClient.post(`/api/polygons/assignments/${assignmentId}/deactivate`),
  
  deleteAssignment: (assignmentId: string) =>
    apiClient.delete(`/api/polygons/assignments/${assignmentId}/delete`)
};

// Route Optimization API calls
export const routeOptimizationApi = {
  // Optimization Management
  createOptimization: (data: {
    name: string;
    num_routes: number;
    filters?: any;
    created_by?: string;
    notes?: string;
  }) =>
    apiClient.post('/api/route-optimization', data),
  
  getAllOptimizations: (status?: string) => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    return apiClient.get('/api/route-optimization', Object.keys(params).length ? params : undefined);
  },
  
  getOptimizationById: (optimizationId: string) =>
    apiClient.get(`/api/route-optimization/${optimizationId}`),
  
  deleteOptimization: (optimizationId: string) =>
    apiClient.delete(`/api/route-optimization/${optimizationId}`),
  
  // Candidate Orders
  getCandidateOrders: (filters: any) =>
    apiClient.post('/api/route-optimization/candidates', { filters }),
  
  selectCandidateOrders: (optimizationId: string, deliveryIds: string[]) =>
    apiClient.post(`/api/route-optimization/${optimizationId}/select-orders`, { delivery_ids: deliveryIds }),
  
  // Auto-Assignment
  autoAssignToExisting: (optimizationId: string, deliveryIds: string[]) =>
    apiClient.post(`/api/route-optimization/${optimizationId}/auto-assign-existing`, { delivery_ids: deliveryIds }),
  
  autoAssignSameDriver: (optimizationId: string, deliveryId: string) =>
    apiClient.post(`/api/route-optimization/${optimizationId}/auto-assign-same-driver`, { delivery_id: deliveryId }),
  
  // Route & Driver Assignment
  createRoutesWithDrivers: (optimizationId: string, driverAssignments: Array<{ driver_id: string }>) =>
    apiClient.post(`/api/route-optimization/${optimizationId}/create-routes`, { driver_assignments: driverAssignments }),
  
  modifyDriverAssignment: (optimizationId: string, routeId: string, driverId: string) =>
    apiClient.put(`/api/route-optimization/${optimizationId}/modify-driver/${routeId}`, { driver_id: driverId }),
  
  reassignDelivery: (optimizationId: string, deliveryId: string, newRouteId: string, newPosition: number) =>
    apiClient.post(`/api/route-optimization/${optimizationId}/reassign-delivery`, {
      delivery_id: deliveryId,
      new_route_id: newRouteId,
      new_position: newPosition
    }),
  
  // Activation
  activateOptimization: (optimizationId: string) =>
    apiClient.post(`/api/route-optimization/${optimizationId}/activate`)
};
